import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import JSONPath from 'jsonpath';
import REST from 'go/requests/rest';
import {connectDynamic} from '../dynamicComponent';

export const CopyAttachmentsListener = (props) => {
  const {
    token,
    fromEntityId,
    toEntityId,
    fromEntityType,
    toEntityType,
    jsonPaths,
    limitToAttachmentTypes,
    attachmentsCopiedFlag,
    saveEntityData,
    entityDataPath,
  } = props;

  // include payload in entityDataPath
  const toEntityPayload = props.data;

  const flag = attachmentsCopiedFlag || 'attachmentsCopied';
  const entityType = props.entityType || props.toEntityType;

  const [attachmentsCopied, setAttachmentsCopied] = useState(false);

  useEffect(() => {
    const copyAttachments = async () => {
      const toEntityPayloadPaths = toEntityPayload
        ? _.flatten(_.map(jsonPaths, (path) => JSONPath.paths(toEntityPayload, path)))
        : [];
      if (
        toEntityPayload &&
        !toEntityPayload[flag] &&
        !_.isEmpty(toEntityPayloadPaths) &&
        !attachmentsCopied
      ) {
        try {
          setAttachmentsCopied(true);
          // Retrieve old attachments
          const oldFromAttachmentsResult =
            await REST.attachment.getAttachmentsByEntityIdAndEntityType(
              token,
              fromEntityId,
              fromEntityType
            );
          const oldFromAttachments = await oldFromAttachmentsResult.json();

          // Copy attachments in database
          const copiedAttachmentsResult = await REST.attachment.copyAttachments(
            fromEntityId,
            fromEntityType,
            toEntityId,
            toEntityType,
            limitToAttachmentTypes
          );
          const copiedAttachments = await copiedAttachmentsResult.json();
          // Update to entity payload
          await updateValues(toEntityPayload, copiedAttachments, oldFromAttachments);
        } catch (e) {
          console.error('Error copying attachments: ' + e);
        }
      } else if (toEntityPayload && !attachmentsCopied) {
        console.log(
          'Copied attachments flag found in payload, or payload does not contain attachment paths. Skipping copying attachments'
        );
      }
    };
    copyAttachments();
  }, [toEntityPayload, attachmentsCopied]);

  const updateValues = (toEntityPayload, copiedAttachments, oldFromAttachments) => {
    let exactPaths = _.concat(
      [],
      ..._.map(jsonPaths, (path) => JSONPath.paths(toEntityPayload, path))
    );
    const updatedEntityPayload = _.cloneDeep(toEntityPayload);
    // Remove trailing dollar sign from each path returned by JSONPath.paths()
    exactPaths = _.map(exactPaths, (path) => _.drop(path));

    _.forEach(exactPaths, (path) => {
      const newValue = getNewValue(
        toEntityPayload,
        path,
        copiedAttachments,
        oldFromAttachments
      );
      if (newValue) {
        _.set(updatedEntityPayload, path, newValue);
      }
    });

    if (_.isEqual(toEntityPayload, updatedEntityPayload)) {
      return null;
    }

    // Set flag to indicate attachments were copied
    updatedEntityPayload[flag] = true;

    saveEntityData(entityDataPath, entityType, updatedEntityPayload);

    return updatedEntityPayload;
  };

  const getNewValue = (
    toEntityPayload,
    path,
    copiedAttachmentsResult,
    oldFromAttachments
  ) => {
    let newArray = [];
    try {
      const oldArray = _.get(toEntityPayload, path);
      _.forEach(oldArray, (oldAttachmentValue) => {
        if (
          typeof oldAttachmentValue === 'string' ||
          typeof oldAttachmentValue === 'number'
        ) {
          const newAttachment = _.get(copiedAttachmentsResult, '' + oldAttachmentValue);
          if (newAttachment) {
            newArray = _.concat(newArray, _.toString(_.get(newAttachment, 'id')));
          } else {
            console.error(
              'id ' +
                oldAttachmentValue +
                ' at path ' +
                path +
                ' was not copied. This attachment will be untouched'
            );
            newArray = _.concat(newArray, oldAttachmentValue);
          }
        } else if (typeof oldAttachmentValue === 'object') {
          const newObject = getNewObject(
            oldAttachmentValue,
            copiedAttachmentsResult,
            oldFromAttachments
          );
          if (newObject) {
            newArray = _.concat(newArray, newObject);
          } else {
            console.error(
              'attachment at path ' +
                path +
                ' was not copied. This attachment will be untouched'
            );
            newArray = _.concat(newArray, oldAttachmentValue);
          }
        } else {
          console.error(
            'found unknown value ' +
              oldAttachmentValue +
              ' at path ' +
              path +
              ' and it will be untouched'
          );
          newArray = _.concat(newArray, oldAttachmentValue);
        }
      });
    } catch (e) {
      console.error('Error copying attachment, will be untouched. ' + e);
      newArray = null;
    }
    return newArray;
  };

  const getNewObject = (oldValue, copiedAttachments, oldFromAttachments) => {
    const resourceId = _.get(oldValue, 'resourceId');
    if (!resourceId) {
      console.error('found attachment with missing resource id');
      return null;
    }

    // first map from payload resource id to old from attachment so we can get id
    // later for the copy result
    const fromAttachment = _.find(
      oldFromAttachments,
      (attachment) => _.get(attachment, 'resourceId') === resourceId
    );
    if (!fromAttachment) {
      console.error('resource id ' + resourceId + ' has no corresponding new attachment');
      return null;
    }

    // newAttachments is keyed by old id
    const newAttachment = _.get(
      copiedAttachments,
      _.toString(_.get(fromAttachment, 'id'))
    );
    if (!newAttachment) {
      console.error(
        'resource id ' +
          resourceId +
          ' with id ' +
          _.get(fromAttachment, 'id') +
          ' has no corresponding new attachment'
      );
      return null;
    }

    const newObject = {};
    newObject.resourceId = _.get(newAttachment, 'resourceId');
    newObject.fileName = _.get(newAttachment, 'fileName');
    newObject.submissionId = _.get(newAttachment, 'submissionId');
    return newObject;
  };

  return null;
};

export default connectDynamic(CopyAttachmentsListener);
